/**
 * AG Page Blocks — shared touch swipe driver for the horizontal carousels.
 *
 * Why this exists: a track that is a native horizontal scroll container
 * (`overflow-x: auto` + `touch-action: pan-x`) lets the browser's own compositor
 * gesture recognizer claim an ambiguous swipe for the carousel. Once it does, the
 * gesture is locked to horizontal for its whole lifetime, so a user who brushes a
 * slide on the way down finds the page scroll dead until they lift their finger.
 * No JS can win that race, because the decision happens off the main thread.
 *
 * The fix is the one Blaze Slider uses (and Frøya ships):
 *   .blaze-track-container { overflow: hidden }   -> nothing horizontal to claim
 *   .blaze-track           { touch-action: pan-y } -> vertical is the ONLY native axis
 * The matching CSS lives in each block's `@media (pointer: coarse)` block. AG's own
 * PD gallery already worked this way, which is why it never had the bug.
 *
 * Horizontal then has to come from JS — that's this file. Vertical is never touched:
 * no preventDefault, no scrollLeft writes, so page scrolling stays fully native and
 * can't be interrupted mid-flick.
 *
 * `overflow: hidden` still permits programmatic scrolling, so callers keep their
 * existing scrollLeft / offsetLeft measurement and settle logic unchanged.
 */
(function () {
  if (window.AgPbSwipe) return;

  /* Movement needed before committing to an axis. Ties go to the page, not the track. */
  var AXIS_LOCK_PX = 6;
  /* Movement needed before a gesture counts as a drag rather than a tap. */
  var DRAG_MIN_PX = 4;
  /* Fraction of one slide that counts as "meant to advance". Matches the value the
     reviews/doctors pointer-drag already used, so mouse and touch feel the same. */
  var STEP_FRACTION = 0.18;
  /* A flick this fast advances even if it barely moved (px/ms). */
  var VELOCITY_THRESHOLD = 0.45;
  /* Floor so very narrow slides still need a deliberate push. */
  var MIN_DISTANCE_PX = 24;

  /**
   * Turn a finished drag into a target index using intent (distance + velocity),
   * not just proximity. Nearest-slide alone forces a >50% drag to advance, which
   * reads as "I have to swipe the whole screen" — native momentum used to hide that.
   */
  function resolveIndex(o) {
    var count = o.count || 0;
    if (count === 0) return 0;

    var last = count - 1;
    var startIndex = Math.max(0, Math.min(o.startIndex || 0, last));
    var step = o.step > 0 ? o.step : 0;
    var distanceThreshold = Math.max(step * STEP_FRACTION, MIN_DISTANCE_PX);
    var deltaX = o.deltaX || 0;
    var velocityX = o.velocityX || 0;

    /* Finger moved left (negative) => advance forward. */
    var forward = deltaX < -distanceThreshold || velocityX < -VELOCITY_THRESHOLD;
    var backward = deltaX > distanceThreshold || velocityX > VELOCITY_THRESHOLD;

    if (forward) return Math.min(startIndex + 1, last);
    if (backward) return Math.max(startIndex - 1, 0);

    var fallback = typeof o.fallbackIndex === 'number' ? o.fallbackIndex : startIndex;
    return Math.max(0, Math.min(fallback, last));
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function cancelGlide(el) {
    if (el && el.__agPbGlideCancel) el.__agPbGlideCancel();
  }

  /**
   * Settle animation with a little carry-over from the flick.
   *
   * `scrollTo({behavior:'smooth'})` runs the same fixed curve whether you flicked hard
   * or barely nudged, which reads as mechanical. This eases out (cubic) over a duration
   * derived from the release speed: a fast flick lands quickly, a slow release drifts.
   * Deliberately mild — the clamp keeps it inside 180–420ms so it never feels floaty.
   */
  function glide(el, target, options) {
    if (!el) return;
    cancelGlide(el);

    var opts = options || {};
    var onDone = typeof opts.onDone === 'function' ? opts.onDone : null;
    var from = el.scrollLeft;
    var distance = target - from;

    if (prefersReducedMotion() || Math.abs(distance) < 1) {
      el.scrollLeft = target;
      if (onDone) onDone();
      return;
    }

    /* Faster release => shorter glide. +0.5 keeps a near-zero velocity from blowing up. */
    var speed = Math.abs(opts.velocityX || 0);
    var duration = Math.min(Math.max(Math.abs(distance) / (speed + 0.5), 180), 420);

    var startedAt = null;
    var rafId = null;
    var cancelled = false;

    function step(now) {
      if (cancelled) return;
      if (startedAt === null) startedAt = now;

      var t = Math.min((now - startedAt) / duration, 1);
      /* easeOutCubic — decelerates like something that was thrown and is coasting. */
      var eased = 1 - Math.pow(1 - t, 3);
      el.scrollLeft = from + distance * eased;

      if (t < 1) {
        rafId = requestAnimationFrame(step);
        return;
      }
      el.scrollLeft = target;
      el.__agPbGlideCancel = null;
      if (onDone) onDone();
    }

    el.__agPbGlideCancel = function () {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      el.__agPbGlideCancel = null;
    };

    rafId = requestAnimationFrame(step);
  }

  function bind(track, options) {
    if (!track || track.dataset.agPbSwipeBound === 'true') return;
    track.dataset.agPbSwipeBound = 'true';

    var opts = options || {};
    var onSettle = typeof opts.onSettle === 'function' ? opts.onSettle : null;
    var onStart = typeof opts.onStart === 'function' ? opts.onStart : null;
    var isEnabled = typeof opts.enabled === 'function' ? opts.enabled : null;
    /* Taps on controls must not start a swipe — touchmove + preventDefault
       cancels the click on iOS/Android (Read more, dots, links). */
    var ignoreSelector = opts.ignoreSelector != null
      ? opts.ignoreSelector
      : 'a, button, input, select, textarea, label, [role="button"]';

    var active = false;
    var axis = null;
    var startX = 0;
    var startY = 0;
    var startScroll = 0;
    var dragged = false;
    var startTime = 0;
    var lastX = 0;
    var lastTime = 0;
    var velocityX = 0;

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    track.addEventListener(
      'touchstart',
      function (e) {
        /* Ignore pinch/multi-touch — let the browser have it. */
        if (!e.touches || e.touches.length !== 1) return;
        if (isEnabled && !isEnabled()) return;
        if (
          ignoreSelector &&
          e.target &&
          e.target.closest &&
          e.target.closest(ignoreSelector)
        ) {
          return;
        }
        /* Grabbing mid-glide must hand control straight back to the finger. */
        cancelGlide(track);
        active = true;
        axis = null;
        dragged = false;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        startScroll = track.scrollLeft;
        startTime = Date.now();
        lastX = startX;
        lastTime = startTime;
        velocityX = 0;
        if (onStart) onStart();
      },
      { passive: true }
    );

    track.addEventListener(
      'touchmove',
      function (e) {
        if (!active || !e.touches || !e.touches[0]) return;

        var x = e.touches[0].clientX;
        var dx = x - startX;
        var dy = e.touches[0].clientY - startY;

        if (axis === null) {
          if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
          /* Strictly greater: a diagonal or ambiguous swipe resolves to vertical. */
          axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (axis === 'y') {
            /* Hand it back to the page permanently — never preventDefault a page scroll. */
            active = false;
            return;
          }
          track.classList.add('is-dragging');
        }

        var now = Date.now();
        var frameDt = now - lastTime;
        if (frameDt > 0) velocityX = (x - lastX) / frameDt;
        lastX = x;
        lastTime = now;

        if (Math.abs(dx) > DRAG_MIN_PX) dragged = true;
        track.scrollLeft = Math.min(Math.max(startScroll - dx, 0), maxScroll());
        if (e.cancelable) e.preventDefault();
      },
      { passive: false }
    );

    function end() {
      if (!active) return;
      active = false;
      var wasDrag = axis === 'x' && dragged;
      axis = null;
      track.classList.remove('is-dragging');
      if (!wasDrag || !onSettle) return;

      var deltaX = lastX - startX;
      var elapsed = Math.max(Date.now() - startTime, 1);
      /* Blend: a fast final flick should beat a slow overall average, and vice versa. */
      var overall = deltaX / elapsed;
      var settleVelocity = Math.abs(velocityX) > Math.abs(overall) ? velocityX : overall;

      onSettle({ deltaX: deltaX, velocityX: settleVelocity, startScroll: startScroll });
    }

    track.addEventListener('touchend', end);
    track.addEventListener('touchcancel', end);
  }

  /* ---------------------------------------------------------------------- *
   * Infinite loop
   *
   * Same idea Blaze Slider uses on Frøya, adapted to our scrollLeft tracks: the
   * real slides are flanked by a cloned copy on each side, so there is always a
   * full set of runway in both directions. After a gesture settles we quietly
   * shift scrollLeft by exactly one set width whenever it drifts out of the
   * middle band. The content either side is identical, so the jump is invisible.
   *
   * Clones are inert: ids stripped (Shopify block wrappers carry unique ids that
   * must not be duplicated), aria-hidden, and nothing inside is focusable. Dots
   * and the active index stay keyed to the REAL slides via `realIndex()`.
   * ---------------------------------------------------------------------- */

  var CLONE_ATTR = 'data-ag-swipe-clone';

  function stripInteractive(el) {
    el.setAttribute(CLONE_ATTR, '');
    el.setAttribute('aria-hidden', 'true');
    el.removeAttribute('id');

    var withIds = el.querySelectorAll('[id]');
    for (var i = 0; i < withIds.length; i++) withIds[i].removeAttribute('id');

    /* Keep clones out of the tab order — they are visual filler, not content. */
    var focusable = el.querySelectorAll('a, button, input, select, textarea, [tabindex]');
    for (var j = 0; j < focusable.length; j++) {
      focusable[j].setAttribute('tabindex', '-1');
      focusable[j].setAttribute('aria-hidden', 'true');
    }
    if (el.matches('a, button, input, select, textarea, [tabindex]')) {
      el.setAttribute('tabindex', '-1');
    }
    return el;
  }

  function enableLoop(track, options) {
    if (!track) return null;

    var opts = options || {};
    var selector = opts.slideSelector;
    if (!selector) return null;

    function realSlides() {
      return Array.prototype.filter.call(track.children, function (el) {
        return el.matches(selector) && !el.hasAttribute(CLONE_ATTR);
      });
    }

    var reals = realSlides();
    /* Below 2 slides there is nothing to loop through. */
    if (reals.length < 2) return null;
    if (track.dataset.agSwipeLoop === 'true') return null;
    track.dataset.agSwipeLoop = 'true';

    var before = document.createDocumentFragment();
    var after = document.createDocumentFragment();
    reals.forEach(function (el) {
      before.appendChild(stripInteractive(el.cloneNode(true)));
      after.appendChild(stripInteractive(el.cloneNode(true)));
    });
    track.insertBefore(before, reals[0]);
    track.appendChild(after);

    var controller = {
      realCount: reals.length,

      /* One full set of travel: first real slide -> first slide of the trailing set. */
      setWidth: function () {
        var current = realSlides();
        if (current.length === 0) return 0;
        var firstReal = current[0];
        var trailing = firstReal;
        var steps = current.length;
        while (steps > 0 && trailing) {
          trailing = trailing.nextElementSibling;
          steps--;
        }
        var end = trailing || track.lastElementChild;
        if (!end) return 0;
        return Math.max(0, end.offsetLeft - firstReal.offsetLeft);
      },

      /* Park on the middle (real) set — visually identical to a non-looping start. */
      reset: function () {
        var current = realSlides();
        if (!current.length) return;
        track.scrollLeft = current[0].offsetLeft - track.firstElementChild.offsetLeft;
      },

      /* Slide index ignoring which copy it belongs to. */
      realIndex: function (allIndex) {
        var n = controller.realCount;
        if (!n) return 0;
        return ((allIndex % n) + n) % n;
      },

      /*
        Real slides live in [setWidth, 2 * setWidth). Leading clones are [0, setWidth),
        trailing clones are [2 * setWidth, 3 * setWidth). Shift by whole sets until
        we sit in the real band. A 0.5/1.5 band was wrong: last real cards sit past
        1.5 * setWidth, so they got yanked onto leading clones.

        Only safe between gestures — mid-drag would yank the finger anchor.
      */
      normalize: function () {
        var w = controller.setWidth();
        if (w <= 0) return;
        var s = track.scrollLeft;
        var guard = 0;
        while (s < w && guard++ < 8) s += w;
        while (s >= w * 2 && guard++ < 8) s -= w;
        if (s !== track.scrollLeft) track.scrollLeft = s;
      }
    };

    track.__agSwipeLoopCtl = controller;
    return controller;
  }

  /* ---------------------------------------------------------------------- *
   * Continuous auto-scroll (slow infinite marquee drift)
   *
   * Opt-in per block (`continuous_scroll` schema setting). Drifts scrollLeft at a constant
   * px/second through the cloned loop, so it needs the infinite loop to be active on the
   * track — if clones are absent (desktop static row, breakpoint without loop) it simply
   * idles. Pauses on hover/touch/focus/drag, while off screen or in a background tab, and
   * never runs for reduced-motion users. Wrap uses the loop's own band maths, so it is
   * invisible. Mid-flight user scrolls (drag, dots, glide) are adopted, not fought.
   * ---------------------------------------------------------------------- */
  var AUTOSCROLL_DEFAULT_SPEED = 30;

  function autoScroll(track, options) {
    if (!track || prefersReducedMotion()) return null;
    if (track.__agPbAutoScroll) return track.__agPbAutoScroll;

    var speed = options && options.speed > 0 ? options.speed : AUTOSCROLL_DEFAULT_SPEED;
    var pos = track.scrollLeft;
    var lastFrame = null;
    var holding = false;
    var holdUntil = 0;
    var inView = true;
    var rafId = null;

    /* Snap/smooth would fight sub-pixel increments. */
    track.style.scrollSnapType = 'none';
    track.style.scrollBehavior = 'auto';

    function hold() { holding = true; }
    function release() { holding = false; }

    track.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') hold(); });
    track.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') release(); });
    /* Keyboard focus only: a tap can leave focus parked on a button (Android) and would
       otherwise freeze the drift until the user taps elsewhere. */
    track.addEventListener('focusin', function (e) {
      if (e.target && e.target.matches && e.target.matches(':focus-visible')) hold();
    });
    track.addEventListener('focusout', release);
    track.addEventListener('touchstart', hold, { passive: true });
    track.addEventListener('touchend', release, { passive: true });
    track.addEventListener('touchcancel', release, { passive: true });

    if ('IntersectionObserver' in window) {
      inView = false;
      new IntersectionObserver(function (entries) {
        inView = entries[entries.length - 1].isIntersecting;
      }, { threshold: 0.1 }).observe(track);
    }

    /* A smooth scrollTo (dots, arrows) takes a frame or two to start moving. Writing scrollLeft
       in that gap cancels it, so dots looked dead. Mark the track busy for any programmatic
       scroll and stay hands-off until it has been still for a moment. */
    var busy = false;
    var lastMoveAt = 0;
    ['scrollTo', 'scrollBy'].forEach(function (name) {
      var native = track[name];
      if (typeof native !== 'function') return;
      track[name] = function () {
        busy = true;
        lastMoveAt = performance.now();
        return native.apply(track, arguments);
      };
    });

    var lastSeen = track.scrollLeft;
    var lastUserMoveAt = -Infinity;
    var STILL_MS = 100;
    var setWidthCache = 0;
    var cachedFor = 0;
    var frames = 0;

    function tick(now) {
      rafId = requestAnimationFrame(tick);
      if (lastFrame === null) { lastFrame = now; return; }
      var dt = Math.min(now - lastFrame, 64);
      lastFrame = now;

      /* Anything other than our own write moved the track (finger, iOS momentum glide, dot
         click, wheel). Never write over it: yield this frame; the drift picks up again on the
         first frame the track is still, with no added delay. */
      var current = track.scrollLeft;
      if (busy) {
        if (Math.abs(current - lastSeen) > 0.5) lastMoveAt = now;
        pos = current;
        lastSeen = current;
        if (now - lastMoveAt < 150) return;
        busy = false;
      }
      /* Any movement that is not our own write is the user (finger, mouse, wheel, momentum).
         Momentum decelerates below 1px per frame, so a distance threshold mistakes the tail of
         a flick for "still" and our writes then fight it (visible flicker). Stay out until the
         track has genuinely stopped for a few frames. */
      if (Math.abs(current - lastSeen) > 0.4) lastUserMoveAt = now;
      if (now - lastUserMoveAt < STILL_MS) {
        pos = current;
        lastSeen = current;
        return;
      }

      var ctl = track.dataset.agSwipeLoop === 'true' ? track.__agSwipeLoopCtl : null;
      var paused = !ctl || !inView || holding || document.hidden || Date.now() < holdUntil ||
        track.__agPbGlideCancel || track.classList.contains('is-dragging') ||
        track.scrollWidth <= track.clientWidth + 1;
      if (paused) { pos = current; lastSeen = current; return; }

      /* Set width only changes on resize/relayout; avoid a layout read every frame. */
      var cw = track.clientWidth;
      if (!setWidthCache || cw !== cachedFor || frames++ % 60 === 0) {
        setWidthCache = ctl.setWidth();
        cachedFor = cw;
      }
      var w = setWidthCache;
      if (w <= 0) { pos = current; lastSeen = current; return; }

      pos += (speed * dt) / 1000;
      var guard = 0;
      while (pos >= w * 2 && guard++ < 8) pos -= w;
      while (pos < w && guard++ < 8) pos += w;
      track.scrollLeft = pos;
      lastSeen = track.scrollLeft;
    }

    rafId = requestAnimationFrame(tick);

    var controller = {
      /* Optional explicit hold for `ms`. */
      pause: function (ms) { holdUntil = Date.now() + ms; },
      stop: function () {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = null;
        track.__agPbAutoScroll = null;
      }
    };
    track.__agPbAutoScroll = controller;
    return controller;
  }

  /* ---------------------------------------------------------------------- *
   * Desktop mouse drag (click-and-drag to scroll a native overflow track)
   *
   * Touch already scrolls natively; a mouse cannot. Mouse only, so touch and pen keep their
   * own behaviour. Free scroll while dragging (snap is lifted for the gesture and restored),
   * and the click that ends a drag is swallowed so it cannot trigger a link or button.
   * ---------------------------------------------------------------------- */
  function mouseDrag(track) {
    if (!track || track.__agPbMouseDrag) return;
    track.__agPbMouseDrag = true;

    var down = false;
    var dragging = false;
    var startX = 0;
    var startScroll = 0;
    var prevSnap = '';

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      dragging = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
    });

    window.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < DRAG_MIN_PX) return;
        dragging = true;
        prevSnap = track.style.scrollSnapType;
        track.style.scrollSnapType = 'none';
        track.classList.add('is-dragging');
      }
      track.scrollLeft = startScroll - dx;
      e.preventDefault();
    }, { passive: false });

    function end() {
      if (!down) return;
      down = false;
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      track.style.scrollSnapType = prevSnap;
      var swallow = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
      track.addEventListener('click', swallow, { capture: true, once: true });
      window.setTimeout(function () { track.removeEventListener('click', swallow, true); }, 0);
    }

    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  window.AgPbSwipe = {
    bind: bind,
    resolveIndex: resolveIndex,
    glide: glide,
    enableLoop: enableLoop,
    autoScroll: autoScroll,
    mouseDrag: mouseDrag,
    CLONE_ATTR: CLONE_ATTR
  };
})();
